"use client";

import type { SearchResponse, SearchTotals, SearchType } from "@/server/search/search.service";

/**
 * جستجوی سریع پنل هدر (مستقیم از مرورگر):
 * - حالت fast=1 در API: فقط نام/عنوان/کد کالا (بدون اسکن توضیحات طولانی)
 * - کش حافظه‌ای: تایپ دوباره‌ی یک عبارت یا برگشت با Backspace فوری جواب می‌دهد
 * - لغو درخواست قبلی با AbortSignal وقتی کاربر به تایپ ادامه می‌دهد
 */

const API = `${process.env.NEXT_PUBLIC_API_URL}/search`;
const TTL_MS = 60_000;
const MAX_ENTRIES = 60;

const EMPTY_TOTALS: SearchTotals = { products: 0, posts: 0, files: 0 };

const cache = new Map<string, { at: number; value: SearchResponse }>();

const empty = (): SearchResponse => ({ products: [], posts: [], files: [], totals: { ...EMPTY_TOTALS } });

export async function suggestSearch(
    keyword: string,
    page = 1,
    pageSize = 20,
    type?: SearchType,
    signal?: AbortSignal
): Promise<SearchResponse> {
    const term = keyword.trim();
    if (!term) return empty();

    const key = `${term.toLowerCase()}|${page}|${pageSize}|${type ?? ""}`;
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < TTL_MS) return hit.value;

    const url =
        `${API}?query=${encodeURIComponent(term)}&page=${page}&pageSize=${pageSize}` +
        `&published=1&fast=1${type ? `&type=${type}` : ""}`;

    let res: Response;
    try {
        res = await fetch(url, { signal, headers: { Accept: "application/json" } });
    } catch (error) {
        if ((error as Error)?.name === "AbortError") throw error;
        console.error("[suggestSearch] network error:", error);
        return empty();
    }

    if (!res.ok) return empty();

    const data = (await res.json()) as Partial<SearchResponse>;
    const products = data.products ?? [];
    const posts = data.posts ?? [];
    const files = data.files ?? [];

    const value: SearchResponse = {
        products,
        posts,
        files,
        totals: {
            products: data.totals?.products ?? products.length,
            posts: data.totals?.posts ?? posts.length,
            files: data.totals?.files ?? files.length,
        },
    };

    if (cache.size >= MAX_ENTRIES) {
        const oldest = cache.keys().next().value;
        if (oldest !== undefined) cache.delete(oldest);
    }
    cache.set(key, { at: Date.now(), value });

    return value;
}
